import clsx from 'clsx'
import { Helmet } from "react-helmet"
import { toast } from 'react-toastify'
import React, { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router"
import { DialogTitle, MenuButton, MenuItem } from "@headlessui/react"

import { ErrorState } from "../errors/ErrorState"
import DropDown from "../../lib/fragments/DropDown"
import { PEOPLE_PROFILE } from "../../api/Registry"
import { APPLICATION } from "../../utils/Constants"
import HttpServices from "../../services/HttpServices"
import { LoadingXL } from "../../components/utils/LoadingXL"
import { usePageContext } from "../../lib/guards/PageContext"
import { ConsentModal } from '../../lib/fragments/ConsentModal'
import { EditProfileDefinition } from './EditProfileDefinition'
import { CreateProfileDefinition } from './CreateProfileDefinition'
import { resolveDataSchemaByKey } from '../../lib/phases/PhaseHelper'
import { CreateOrEditProfileGroup } from './CreateOrEditProfileGroup'
import { getStandardRoute, RouteHydration } from "../../utils/Helpers"
import { ModuleBreadCrumb } from "../../lib/fragments/ModuleBreadCrumb"

export const PeopleProfileManager = () => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        activeProfileGroup: null,
        data: {
            people_profiles: null,
        },
        show: {
            uuid: null,
            modal: null,
            action: 'create',
        }
    })

    const navigate = useNavigate()
    const location = useLocation()
    const [actingState, setActingState] = useState(false)

    const pageTitle = "People Profile"
    const pageDescr = "Customize your directory by defining the fields and groups that best reflect your people and organization."

    type ModalKeys = 'createOrEditProfileGroup' | 'disableProfileGroup' | 'createProfileDefinition' | 'editProfileDefinition'
    const { updateDataContext } = usePageContext();

    const breadCrumb = [
        { linkItem: false, title: 'IAM' },
        { linkItem: false, title: 'Identity' },
        { linkItem: false, title: pageTitle },
    ]

    const getProfileGroupsAndDefinitions = async () => {
        let { status, httpStatus, data, activeProfileGroup } = state

        try {
            const response: any = await HttpServices.httpGet(PEOPLE_PROFILE.GROUP_COLLECTION)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload
                data.people_profiles = payload.profiles

                status = 'fulfilled'
                dataContextHandler(status)
                showGroupListingProfiles(data.people_profiles[0]?.g)
            } else {
                status = 'rejected'
                dataContextHandler(status)
            }
        } catch (error) {
            httpStatus = 500
            status = 'rejected'
            dataContextHandler(status)
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            data: {
                ...prevState.data,
            },
        }))
    }

    useEffect(() => {
        dataContextHandler()
        getProfileGroupsAndDefinitions()
    }, [])

    const dataContextHandler = (status: string = 'pending') => {
        const baseContext = {
            title: pageTitle,
        }

        // Early return if data is not ready
        if (status !== 'fulfilled') {
            updateDataContext(baseContext)
            return
        }

        updateDataContext(baseContext)
    }

    const toggleModal = (key: ModalKeys, IsVisible: boolean, uuid?: any, action?: string) => {
        if (state.status !== 'fulfilled' || state.posting) return

        setstate((prevState: any) => ({
            ...prevState,
            show: {
                ...prevState.show,
                uuid: uuid,
                action: action ?? 'create',
                modal: IsVisible ? key : null,
            },
        }))
    }

    const onCompleteActionHandler = () => {
        getProfileGroupsAndDefinitions()
    }

    const showGroupListingProfiles = (targetGroup: any) => {
        if (state.posting) return;
        const activeGroup = state.data.people_profiles.find((group: any) => group.g === targetGroup);

        if (activeGroup || targetGroup === null) {
            setstate((prevState: any) => ({
                ...prevState,
                activeProfileGroup: activeGroup
            }))
        }
    }

    const profileGroupActionHandler = (action: string, group: any = null) => {
        // Exit if group is not enabled
        if (!group?.n) return

        // Cannot amend system groups
        if (group?.d === 'system') return

        toggleModal('createOrEditProfileGroup', true, group?.g, action)
    }

    const attributeGroupStatusHandler = async (group: any, action: string) => {
        if (action === 'disable') {
            if (actingState) return
            setActingState(true);
        }

        try {
            const attributeGroupRoute = [":group"]
                .reduce((route, param, index) =>
                    RouteHydration(route, param, [group][index]),
                    PEOPLE_PROFILE.GROUP_RESOURCE
                );

            let formData = new FormData()
            formData.append('action', action)

            const response: any = await HttpServices.httpPatch(attributeGroupRoute, formData)
            const success = response.status === 200;

            if (!success) toast.error(APPLICATION.ERR_MSG);
        } catch (error) {
            toast.error(APPLICATION.ERR_MSG);
        } finally {
            setActingState(false);
            toggleModal('disableProfileGroup', false);
            getProfileGroupsAndDefinitions()
        }
    }

    const backToGroupsHandler = () => {
        if (state.posting) return

        setstate((prev: any) => ({
            ...prev,
            activeProfileGroup: null
        }))
    }

    const navigateToEditUserAttribute = (attr: any) => {
        const navigationState = {
            previous_path: location.pathname,
            title: attr.e
        }

        let navigationRoute = getStandardRoute('USER.EDIT_ATTR_')

        navigationRoute = [":attribute"]
            .reduce((route, param, index) =>
                RouteHydration(route, param, [attr.g][index]),
                navigationRoute
            );

        return navigate(navigationRoute, { replace: false, state: navigationState });
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                {
                    state.status === 'rejected' ? (
                        <>
                            <div className="w-full py-2 border-b border-stone-200 dark:border-stone-600 lg:px-6 px-5 flex flex-col">
                                <div className="flex-none">
                                    <ModuleBreadCrumb breadCrumb={breadCrumb} />
                                </div>
                            </div>

                            <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                                <ErrorState
                                    code={state.httpStatus}
                                />
                            </div>
                        </>
                    ) : state.status === 'fulfilled' ? (
                        <>
                            <div className="w-full py-2 border-b border-stone-200 dark:border-stone-600 lg:px-6 px-5 flex flex-col">
                                <div className="flex-none">
                                    <ModuleBreadCrumb breadCrumb={breadCrumb} />
                                </div>
                            </div>

                            <div className="flex h-full w-full bg-white overflow-hidden">
                                {/* PANE 1: GROUP NAVIGATOR (Stationary Sidebar) */}
                                <aside className={`${state.activeProfileGroup ? 'hidden md:flex' : 'flex'} w-full md:w-72 lg:w-[350px] flex-col border-r border-stone-200 bg-stone-50/50`}>
                                    <div className="px-6 md:py-4 pt-3">
                                        <p className="text-stone-600 dark:text-stone-300 pb-1 text-sm">
                                            {pageDescr}
                                        </p>
                                    </div>

                                    <nav className="flex-1 overflow-y-auto px-4 pt-4">
                                        {
                                            state.data.people_profiles.map((group: any) => (
                                                <button key={group.g} onClick={() => showGroupListingProfiles(group.g)}
                                                    className={clsx(
                                                        // Disabled State: Faded look without changing size
                                                        !group.n && "opacity-40 grayscale-[0.5] font-semibold",
                                                        state.activeProfileGroup?.g === group.g
                                                            ? 'text-stone-700 dark:text-orange-200 bg-stone-200/70 dark:bg-orange-900 px-1'
                                                            : 'hover:bg-stone-100 dark:hover:bg-stone-900/50 text-stone-600 dark:text-stone-400',
                                                        "w-full py-3 flex items-center gap-x-2 px-3 rounded-lg group transition-all duration-200 relative"
                                                    )}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-start block text-sm truncate font-medium">{group.e}</p>
                                                    </div>

                                                    {
                                                        group.n && (
                                                            <svg xmlns="http://www.w3.org" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                                                            </svg>
                                                        )
                                                    }
                                                </button>
                                            ))
                                        }

                                        <div className="flex flex-row-reverse py-4">
                                            <button type="button" onClick={() => toggleModal('createOrEditProfileGroup', true)} className={`w-auto min-w-24 flex align-middle items-center gap-x-2 px-3 py-2 text-sm font-medium text-orange-500 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-in-out`}>
                                                <span className="fa-doutone fa-plus fa-xl"></span>

                                                <span className="text-sm">
                                                    Create Group
                                                </span>
                                            </button>
                                        </div>
                                    </nav>
                                </aside>

                                {/* PANE 2: FIELD WORKSPACE (Scrollable Content) */}
                                <main className={`${!state.activeProfileGroup ? 'hidden md:flex' : 'flex'} flex-1 flex-col h-full bg-white`}>
                                    {
                                        state.activeProfileGroup ? (
                                            <>
                                                {/* Header: Always visible */}
                                                <header className="md:px-8 px-5 py-3 flex flex-col md:flex-col lg:flex-row justify-between sticky top-0 bg-white/80 backdrop-blur-md z-10">
                                                    <div>
                                                        <button onClick={backToGroupsHandler} className="md:hidden text-orange-600 font-black text-xs uppercase tracking-widest md:mb-4 mb-2 pt-1 flex items-center gap-2">
                                                            <span className="fa-regular fa-lg">←</span> Back to Groups
                                                        </button>

                                                        <div className="min-w-0 pt-1">
                                                            <h2 className="text-stone-900 dark:text-white text-lg md:text-xl md:font-normal font-medium tracking-wide flex align-middle items-center gap-x-2">
                                                                {state.activeProfileGroup.e}
                                                            </h2>

                                                            <p className="text-stone-600 dark:text-stone-300 py-2 md:pt-4 lg:pt-2 text-sm">
                                                                {state.activeProfileGroup.t ?? '-'}
                                                            </p>

                                                            <div className="w-full py-2 flex gap-3 md:mb-0 mb-4">
                                                                <span className={
                                                                    clsx(
                                                                        state.activeProfileGroup.a.toLowerCase().includes('enterprise') && "bg-blue-50 text-blue-700",
                                                                        state.activeProfileGroup.a.toLowerCase().includes('partner') && "bg-amber-50 text-amber-700",
                                                                        state.activeProfileGroup.a.toLowerCase().includes('global') && "bg-emerald-50 text-emerald-700",
                                                                        "px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider flex items-center gap-1"
                                                                    )
                                                                }>
                                                                    {state.activeProfileGroup.a}
                                                                </span>

                                                                {
                                                                    !state.activeProfileGroup.n && (
                                                                        <span className="px-2 py-0.5 rounded-md bg-red-50 dark:bg-red-800/20 text-red-600 dark:text-red-400 text-[10px] font-medium uppercase tracking-wider flex items-center gap-1">
                                                                            Disabled
                                                                        </span>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className={`flex flex-row-reverse md:pt-2 ${state.activeProfileGroup.s.length < 1 && 'hidden'}`}>
                                                        <DropDown
                                                            type="custom"
                                                            button={
                                                                <div className="flex flex-row h-9 w-auto items-center rounded-lg bg-white dark:bg-stone-900 border border-orange-400 dark:border-orange-700 shadow-sm transition-all hover:border-orange-500 dark:hover:border-orange-600 overflow-hidden">
                                                                    <button
                                                                        type="button"
                                                                        disabled={!state.activeProfileGroup.n}
                                                                        onClick={() => toggleModal('createProfileDefinition', true)}
                                                                        className="px-4 h-full flex items-center gap-x-2 text-[13px] font-medium text-orange-500 dark:text-orange-300 hover:text-orange-700 hover:bg-slate-50 dark:hover:bg-stone-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-r border-orange-400 dark:border-orange-700"
                                                                    >
                                                                        <span className=" font-">+</span>
                                                                        New Profile
                                                                    </button>

                                                                    <div className="h-full flex items-center">
                                                                        <MenuButton className="w-10 h-full flex items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-orange-200 hover:bg-slate-50 dark:hover:bg-stone-800 data-[open]:bg-slate-100 dark:data-[open]:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                                                                            <svg
                                                                                viewBox="0 0 20 20"
                                                                                fill="currentColor"
                                                                                className="w-5 h-5 opacity-70 group-hover:opacity-100"
                                                                            >
                                                                                <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                                                            </svg>
                                                                        </MenuButton>
                                                                    </div>
                                                                </div>

                                                            }
                                                            items={
                                                                <div className="w-60">
                                                                    <MenuItem>
                                                                        <button type="button" onClick={() => profileGroupActionHandler('edit', state.activeProfileGroup)} disabled={state.activeProfileGroup.d === 'system'} className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start disabled:cursor-not-allowed disabled:opacity-50">
                                                                            Edit Group Metadata
                                                                        </button>
                                                                    </MenuItem>

                                                                    <MenuItem>
                                                                        <button type="button" onClick={() => profileGroupActionHandler('edit', state.activeProfileGroup)} disabled={!state.activeProfileGroup.n} className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start disabled:cursor-not-allowed disabled:opacity-50">
                                                                            Reorder Items
                                                                        </button>
                                                                    </MenuItem>

                                                                    <MenuItem>
                                                                        <button type="button" onClick={() => profileGroupActionHandler('edit', state.activeProfileGroup)} disabled={!state.activeProfileGroup.n} className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start disabled:cursor-not-allowed disabled:opacity-50">
                                                                            Activity Log
                                                                        </button>
                                                                    </MenuItem>

                                                                    <div className="pt-1 mb-1 w-[90%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                                                    {
                                                                        state.activeProfileGroup.n ? (
                                                                            <MenuItem>
                                                                                <button type="button" onClick={() => toggleModal('disableProfileGroup', true, state.activeProfileGroup.g)}
                                                                                    className="w-full rounded-md px-3 text-red-700 dark:text-red-600 py-2 text-sm data-[focus]:bg-red-200 dark:data-[focus]:bg-red-900 data-[focus]:text-red-900 dark:data-[focus]:text-white hover:bg-red-200 dark:hover:bg-red-800 text-start">
                                                                                    Disable Group
                                                                                </button>
                                                                            </MenuItem>
                                                                        ) : (
                                                                            <MenuItem>
                                                                                <button type="button" onClick={() => attributeGroupStatusHandler(state.activeProfileGroup.g, 'enable')} className="w-full text-emerald-600 rounded-md px-3 py-2 text-sm data-[focus]:bg-emerald-200 dark:data-[focus]:bg-emerald-800/40 dark:data-[focus]:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-800/40 text-start">
                                                                                    Enable Group
                                                                                </button>
                                                                            </MenuItem>
                                                                        )
                                                                    }
                                                                </div>
                                                            }
                                                        />
                                                    </div>
                                                </header>

                                                <div className="md:px-8 px-5 h-full pb-6">
                                                    {
                                                        state.activeProfileGroup.s.length > 0 ? (
                                                            <>
                                                                <div className="w-full grid grid-cols-[minmax(170px,1fr)_32px] md:grid-cols-[minmax(170px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_32px] gap-4 dark:bg-slate-900/30 border-b border-slate-300 dark:border-slate-600 py-2.5 text-left text-sm text-slate-400 dark:text-slate-400 tracking-wider md:px-2">
                                                                    <div className="block">
                                                                        Name
                                                                    </div>

                                                                    <div className="hidden md:block">
                                                                        Type
                                                                    </div>

                                                                    <div className="hidden md:block">
                                                                        Obligation
                                                                    </div>

                                                                    <div className="hidden md:block">
                                                                        User Control
                                                                    </div>

                                                                    <span className="md:block">-</span>
                                                                </div>

                                                                {
                                                                    state.activeProfileGroup.s.map((attr: any) => (
                                                                        <div key={`prof-def-${attr.g}`} className={clsx(
                                                                            !attr.n && "opacity-60 grayscale-[0.5]",
                                                                            "grid grid-cols-[minmax(170px,1fr)_32px] md:grid-cols-[minmax(170px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_32px] gap-x-4 gap-y-1 md:px-2 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-slate-200 dark:border-slate-700 text-sm align-middle items-center  disabled:cursor-pointer"
                                                                        )
                                                                        }>
                                                                            <div className="text-slate-700 font-medium dark:text-slate-200 md:block flex flex-col align-middle md:items-center">
                                                                                <div className="flex items-center align-middle gap-x-2">
                                                                                    <span className="block">
                                                                                        {attr.e}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div className="text-slate-500 dark:text-slate-300 hidden md:block">
                                                                                <div className="flex gap-x-3 items-center align-middle">
                                                                                    <span className="flex gap-x-2 items-center align-middle">
                                                                                        <span className={`fa-light ${resolveDataSchemaByKey(attr.f?.type)?.icon}`}></span>

                                                                                        {resolveDataSchemaByKey(attr.f?.type)?.label}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div className="text-slate-500 dark:text-slate-300 hidden md:block">
                                                                                <div className="flex gap-x-3 items-center align-middle">
                                                                                    <span className="block">
                                                                                        {attr.m === 'required' ? 'Required' : 'Optional'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div className="text-slate-500 dark:text-slate-300 hidden md:block">
                                                                                <div className="flex gap-x-3 items-center align-middle">
                                                                                    <span className="block">
                                                                                        {attr.u === 'editable' ? 'Editable' : 'Read-Only'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div className="h-full flex items-center">
                                                                                <div className="flex-none">
                                                                                    <DropDown
                                                                                        type={'borderless'}
                                                                                        items={
                                                                                            <div className="w-40">
                                                                                                <MenuItem>
                                                                                                    <button type="button" onClick={() => toggleModal('editProfileDefinition', true, attr)} className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                                                                        Edit Attribute
                                                                                                    </button>
                                                                                                </MenuItem>

                                                                                                <MenuItem>
                                                                                                    <button type="button" onClick={() => navigateToEditUserAttribute(attr)} className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                                                                        Change Group
                                                                                                    </button>
                                                                                                </MenuItem>

                                                                                                <div className="pt-1 mb-1 w-[90%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                                                                                <MenuItem>
                                                                                                    <button type="button" /* onClick={() => handleProviderCatalog(catalog)} */
                                                                                                        className="w-full rounded-md px-3 text-red-700 dark:text-red-600 py-2 text-sm data-[focus]:bg-red-200 dark:data-[focus]:bg-red-900 data-[focus]:text-red-900 dark:data-[focus]:text-white hover:bg-red-200 dark:hover:bg-red-800 text-start">
                                                                                                        Disable
                                                                                                    </button>
                                                                                                </MenuItem>
                                                                                            </div>
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </>
                                                        ) : (
                                                            <div className="flex-1 flex flex-col h-full items-center justify-center text-stone-400 border-2 border-slate-300 dark:border-slate-600 rounded-md border-dashed">
                                                                {/* The "Blank Paper" Icon Container */}
                                                                <div className="relative mb-6">
                                                                    {/* Soft Shadow Glow */}
                                                                    <div className="absolute inset-0 bg-blue-100/50 blur-3xl rounded-full scale-150" />

                                                                    <div className="relative w-24 h-24 bg-white dark:bg-stone-900 border-2 border-dashed border-slate-200 dark:border-stone-800 rounded-lg flex items-center justify-center">
                                                                        <svg xmlns="http://www.w3.org" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-10 h-10 text-slate-300 dark:text-stone-600">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                                                                        </svg>

                                                                        {/* Small Floating "+" Badge */}
                                                                        <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-slate-300 rounded-full border-4 border-white dark:border-stone-900 flex items-center justify-center shadow-lg">
                                                                            <span className="text-white font-bold text-lg">+</span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <p className="text-sm text-slate-500 dark:text-stone-400 mt-2 max-w-[280px] leading-relaxed text-center">
                                                                    Build your People Profile group by adding a new profile field
                                                                </p>

                                                                <button type="button" onClick={() => toggleModal('createProfileDefinition', true)} className={`w-auto mt-6 min-w-28 bg-orange-600 dark:bg-orange-500 flex align-middle items-center gap-x-2 px-4 py-2 text-sm text-white dark:text-white hover:text-white dark:hover:text-white focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-in-out rounded-md shadow-lg shadow-orange-300`}>
                                                                    <span className="fa-doutone fa-plus fa-xl"></span>

                                                                    <span className="text-sm">
                                                                        New Profile
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex-1 flex flex-col items-center justify-center text-stone-400">
                                                    <span className="text-6xl mb-4">▦</span>
                                                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-xs">Select a group to configure profiles</p>
                                                </div>
                                            </>
                                        )
                                    }
                                </main>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col justify-center w-full overflow-auto py-4">
                            <LoadingXL />
                        </div>
                    )
                }
            </div>

            <ConsentModal
                actionName={
                    actingState ? (
                        <span className="flex flex-row items-center h-6 justify-center gap-x-3">
                            <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                        </span>
                    ) : ('Disable')
                }
                show={state.show.modal === 'disableProfileGroup' ? true : false}
                execAction={() => attributeGroupStatusHandler(state.show.uuid, 'disable')}
                showOrHide={() => toggleModal('disableProfileGroup', false)}
                content={
                    <>
                        <DialogTitle as="h3" className="text-lg leading-6 tracking-wide text-red-600 pb-2">
                            Disable Profile Group?
                        </DialogTitle>

                        <div className="py-2">
                            <p className="text-sm text-slate-600 dark:text-slate-300 text-center">
                                Disabling this group will also disable all associated profile fields. This may impact active user accounts and system configurations.

                                <span className="block mt-3 text-xs text-slate-500 dark:text-slate-400">
                                    {/* This may impact active user accounts and system configurations. */}
                                </span>
                            </p>
                        </div>
                    </>
                }
            />

            <CreateOrEditProfileGroup
                cargo={state.show}
                onComplete={onCompleteActionHandler}
                show={state.show.modal === 'createOrEditProfileGroup' ? true : false}
                showOrHide={() => toggleModal('createOrEditProfileGroup', false)}
            />

            <CreateProfileDefinition
                group={state.activeProfileGroup}
                onComplete={onCompleteActionHandler}
                show={state.show.modal === 'createProfileDefinition' ? true : false}
                showOrHide={() => toggleModal('createProfileDefinition', false)}
            />

            <EditProfileDefinition
                definition={state.show.uuid}
                show={state.show.modal === 'editProfileDefinition' ? true : false}
                showOrHide={() => toggleModal('editProfileDefinition', false)}
            />
        </React.Fragment>
    );
}